import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Puzzle, Power } from "lucide-react";
import { GlassCard, SectionTitle } from "./_shared";
import { Switch } from "@/components/ui/switch";

export default function AdminModules() {
  const [mods, setMods] = useState([]);

  const load = async () => {
    const { data } = await api.get("/admin/modules");
    setMods(data);
  };
  useEffect(() => {
    load();
  }, []);

  const toggle = async (m, v) => {
    setMods((xs) => xs.map((x) => (x.id === m.id ? { ...x, enabled: v } : x)));
    try {
      await api.put(`/admin/modules/${m.id}`, { enabled: v });
      toast.success(`${m.label} ${v ? "activé" : "désactivé"}`);
    } catch {
      toast.error("Erreur");
      load();
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    mods.forEach((m) => {
      g[m.category] = g[m.category] || [];
      g[m.category].push(m);
    });
    return g;
  }, [mods]);

  const active = mods.filter((m) => m.enabled).length;
  const total = mods.length;

  return (
    <div className="space-y-4">
      <SectionTitle icon={Puzzle}>
        Modules · <span className="text-[var(--text)] tabular-nums">{active}/{total}</span>
      </SectionTitle>

      <GlassCard className="p-4 bg-gradient-to-br from-emerald-500/10 via-transparent to-orange-600/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[var(--surface)] border border-[var(--border)] grid place-items-center">
            <Power className="w-5 h-5 text-[var(--text)]" />
          </div>
          <div>
            <div className="text-[var(--text)] font-semibold text-sm">Feature flags</div>
            <div className="text-[11px] text-zinc-400">Activer / désactiver des modules en un clic — propagation instantanée.</div>
          </div>
        </div>
      </GlassCard>

      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat} className="space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 px-1 mt-3">{cat}</div>
          <GlassCard className="divide-y divide-white/5">
            {list.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3.5" data-testid={`module-${m.id}`}>
                <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${m.enabled ? "bg-emerald-500/15 border border-emerald-500/40" : "bg-[var(--surface-alt)] border border-[var(--border)]"}`}>
                  <Power className={`w-4 h-4 ${m.enabled ? "text-emerald-300" : "text-zinc-400"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[var(--text)] truncate">{m.label}</div>
                  <div className="text-[11px] text-zinc-400 truncate">{m.description}</div>
                </div>
                <Switch
                  checked={!!m.enabled}
                  onCheckedChange={(v) => toggle(m, v)}
                  data-testid={`toggle-module-${m.id}`}
                />
              </div>
            ))}
          </GlassCard>
        </div>
      ))}
    </div>
  );
}
